import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signIn, signUp, joinAgency } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Users, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

export default function Login() {
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(!location.state?.isSignup);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('');
  const [agencyName, setAgencyName] = useState('');
  const [createAgency, setCreateAgency] = useState(false);
  const [agencyCode, setAgencyCode] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    if (currentUser) {
      navigate('/app/collaborations');
    }
  }, [currentUser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    setError('');
    setIsSubmitting(true);

    try {
      if (isLogin) {
        await signIn(email, password);
        navigate('/app/collaborations');
      } else {
        await signUp(
          email,
          password,
          firstName,
          lastName,
          role,
          createAgency ? agencyName : undefined,
          !createAgency ? agencyCode : undefined
        );

        // Attendre un peu pour laisser le temps à Firebase de propager les données
        setTimeout(() => {
          navigate('/app/collaborations');
        }, 1500);
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Handle network errors specifically
      if (error.code === 'auth/network-request-failed') {
        setError('Erreur de connexion réseau. Veuillez vérifier votre connexion internet et réessayer.');
        return;
      }
      
      if (error.code === 'auth/email-already-in-use') {
        setError('Un compte existe déjà avec cette adresse email. Veuillez vous connecter.');
        setTimeout(() => {
          setIsLogin(true);
          setError('');
        }, 3000);
        return;
      }
      
      if (error.code === 'auth/invalid-credential') {
        setError('Email ou mot de passe incorrect.');
        return;
      }
      
      if (error.code === 'auth/too-many-requests') {
        setError('Trop de tentatives de connexion. Veuillez attendre quelques minutes avant de réessayer.');
        return;
      }
      
      setError(error.message || 'Erreur d\'inscription, veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setFirstName('');
    setLastName('');
    setRole('');
    setAgencyCode('');
    setAgencyName('');
    setCreateAgency(false);
  };

  const inputClasses = `mt-1 block w-full rounded-lg ${
    theme === 'dark'
      ? 'bg-dark-200 border-dark-300 text-white'
      : 'bg-white border-gray-300 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300'
  } focus:ring-2 focus:ring-inset focus:ring-accent-blue focus:border-accent-blue`;

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-dark' : 'bg-gray-50'} flex flex-col justify-center py-12 sm:px-6 lg:px-8`}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-white" />
        </div>
        <h2 className={`text-3xl font-extrabold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          {isLogin ? 'Connexion à votre compte' : 'Créer un compte'}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className={`${theme === 'dark' ? 'bg-dark-50' : 'bg-white'} py-8 px-4 shadow sm:rounded-xl sm:px-10 ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200 shadow-md'} border`}>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-900/50 border border-red-500/20 text-red-200 px-4 py-3 rounded-lg relative" role="alert">
                <p className="block sm:inline">{error}</p>
              </div>
            )}
            
            {!isLogin && (
              <>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="firstName" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                      Prénom
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={inputClasses}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                      Nom
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={inputClasses}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="role" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                    Rôle dans l'entreprise
                  </label>
                  <input
                    type="text"
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="Ex: Chef de projet, Account Manager..."
                    className={inputClasses}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                    Agence
                  </label>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        id="createAgency"
                        checked={createAgency}
                        onChange={() => {
                          setCreateAgency(true);
                          setAgencyCode('');
                        }}
                        className="h-4 w-4 text-accent-blue focus:ring-accent-blue border-dark-300"
                      />
                      <label htmlFor="createAgency" className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                        Créer une nouvelle agence
                      </label>
                    </div>

                    {createAgency && (
                      <div>
                        <label htmlFor="agencyName" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                          Nom de l'agence
                        </label>
                        <input
                          type="text"
                          id="agencyName"
                          value={agencyName}
                          onChange={(e) => setAgencyName(e.target.value)}
                          className={inputClasses}
                          placeholder="Nom de votre agence"
                          required={createAgency}
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        id="joinAgency"
                        checked={!createAgency}
                        onChange={() => {
                          setCreateAgency(false);
                          setAgencyName('');
                        }}
                        className="h-4 w-4 text-accent-blue focus:ring-accent-blue border-dark-300"
                      />
                      <label htmlFor="joinAgency" className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                        Rejoindre une agence existante
                      </label>
                    </div>

                    {!createAgency && (
                      <div>
                        <label htmlFor="agencyCode" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                          Code de l'agence
                        </label>
                        <input
                          type="text"
                          id="agencyCode"
                          value={agencyCode}
                          onChange={(e) => setAgencyCode(e.target.value)}
                          className="mt-1 block w-full rounded-lg bg-white border-2 border-black text-gray-900 focus:ring-2 focus:ring-inset focus:ring-accent-blue focus:border-accent-blue"
                          placeholder="Code de l'agence à rejoindre"
                          required={!createAgency}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                Adresse email
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                Mot de passe
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClasses}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute inset-y-0 right-0 px-3 flex items-center ${
                    theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-700'
                  }`}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-accent-purple to-accent-blue hover:from-accent-purple/90 hover:to-accent-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue focus:ring-offset-dark"
              >
                {isLogin ? 'Se connecter' : 'S\'inscrire'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={switchMode}
                className="text-sm text-accent-blue hover:text-accent-blue/90"
              >
                {isLogin 
                  ? 'Pas encore de compte ? Inscrivez-vous'
                  : 'Déjà un compte ? Connectez-vous'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}